// El MOMENTO de un hecho, separado del momento en que se registró y del
// momento en que se editó (entrega 2 del motor, lote 1).
//
// POR QUÉ EXISTE ESTE ARCHIVO. Hasta hoy una entrada de la cronología guardaba
// `hora` ("HH:MM", corregible por la autora) y `ts` (sello de creación del
// sistema), y TODO el motor ordenaba comparando `hora` como TEXTO. En un turno
// de Noche o de 24 horas eso pone las 00:10 antes que las 23:50: la cronología,
// el relato que viaja al reparto y el texto copiable contaban el turno al
// revés (hallazgo F-01/H-D3 de la auditoría, caso 8 de la entrega 2).
//
// TRES TIEMPOS DISTINTOS, que este módulo no deja confundir:
//   · momento del HECHO      → cuándo ocurrió el cuidado. Lo declara la autora,
//                              es editable, y puede ser exacto, aproximado o
//                              desconocido.
//   · momento de REGISTRO    → cuándo se escribió. Lo sella el sistema (`ts`).
//   · momento de EDICIÓN     → cuándo se corrigió. Lo sella el sistema.
//
// Y DOS PREGUNTAS DISTINTAS, que tampoco se mezclan:
//   · ¿en qué orden LO MUESTRO?  → ordenarPorHecho (orden visual estable).
//   · ¿qué pasó DESPUÉS de qué?  → esPosteriorA (predicado clínico, estricto).
// El orden de presentación NO demuestra el orden clínico: un evento con hora
// desconocida se ANCLA por su momento de registro para poder dibujarlo en la
// lista, y eso jamás lo convierte en posterior ni en estado vigente.

export const PRECISIONES = ['exacta', 'aproximada', 'desconocida'];
// Dos horas aproximadas separadas por menos que esto no se pueden ordenar entre
// sí: "como a las tres" y "como a las tres y media" no prueban una secuencia.
export const TOLERANCIA_APROXIMADA_MIN = 60;

export function zonaDelDispositivo() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
}

export function fechaISOLocal(d = new Date()) {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}

export function horaLocalHHMM(d = new Date()) {
  const x = d instanceof Date ? d : new Date(d);
  return `${String(x.getHours()).padStart(2, '0')}:${String(x.getMinutes()).padStart(2, '0')}`;
}

// La forma NO basta: "99:99" y "2026-02-31" pasan cualquier regex de dígitos y
// afirmarían una hora y una fecha que no existen. Se valida el calendario.
const RE_FECHA_FORMA = /^\d{4}-\d{2}-\d{2}$/;
const RE_HORA = /^([01]\d|2[0-3]):[0-5]\d$/;

function fechaReal(f) {
  if (!RE_FECHA_FORMA.test(f || '')) return false;
  const [y, m, d] = f.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1) return false;
  return d <= new Date(Date.UTC(y, m, 0)).getUTCDate();
}
const RE_FECHA = { test: fechaReal };

// Momento válido leído del jsonb: se re-valida SIEMPRE (un registro adulterado
// jamás afirma una fecha). Clave nueva ausente ⇒ null, y el llamador deriva.
export function momentoValido(m) {
  if (!m || typeof m !== 'object') return null;
  if (!RE_FECHA.test(m.fecha || '')) return null;
  const precision = PRECISIONES.includes(m.precision) ? m.precision : 'exacta';
  const hora = RE_HORA.test(m.hora || '') ? m.hora : null;
  if (precision !== 'desconocida' && !hora) return null;
  const out = { fecha: m.fecha, hora: precision === 'desconocida' ? null : hora, precision };
  if (typeof m.zona === 'string' && m.zona) out.zona = m.zona;
  if (m.inferida === true) out.inferida = true;
  return out;
}

// "Ahora": lo que la captura usa por defecto, sin preguntar nada.
export function momentoAhora(ahora = new Date()) {
  const zona = zonaDelDispositivo();
  const m = { fecha: fechaISOLocal(ahora), hora: horaLocalHHMM(ahora), precision: 'exacta' };
  return zona ? { ...m, zona } : m;
}

export function nuevoMomento({ fecha, hora, precision = 'exacta', zona, ahora = new Date() } = {}) {
  const base = momentoAhora(ahora);
  const p = PRECISIONES.includes(precision) ? precision : 'exacta';
  const m = {
    fecha: RE_FECHA.test(fecha || '') ? fecha : base.fecha,
    hora: p === 'desconocida' ? null : (RE_HORA.test(hora || '') ? hora : base.hora),
    precision: p,
  };
  const z = zona || base.zona;
  return z ? { ...m, zona: z } : m;
}

// ── Datos antiguos ──────────────────────────────────────────────────────────
// Una entrada escrita antes de este lote solo tiene `hora` y `ts`. La fecha se
// DERIVA del turno del registro, que ya sabe si cruza medianoche
// (finDeTurnoRegistro y su pivote). El resultado queda marcado `inferida: true`
// y NUNCA se persiste como dato confirmado: es una lectura, no una corrección.
//
// Con contexto suficiente (turno declarado que cruza medianoche + `creado`), la
// hora anterior al pivote del turno pertenece al día siguiente. Sin contexto
// (sin turno, sin `creado`, o turno que no cruza), se usa el día de `creado` y
// se marca `ambigua: true`: se ordena igual que hoy y la interfaz puede pedir la
// fecha en vez de inventarla.
// FUENTE ÚNICA del pivote de los turnos que cruzan medianoche: la Noche
// (20:00 a 08:00) pivotea a mediodía y el de 24 horas (08:00 a 08:00) a las
// 08:00, que es su propio inicio. `finDeTurnoRegistro` lo consume desde acá:
// dos copias del mismo mapa se desincronizan sin que nadie se entere, y este
// número decide de qué DÍA es una hora. Vive en este módulo porque no importa
// nada del repo (registroTurno ya importa de acá, al revés sería un ciclo).
export const PIVOTE_DIA_SIGUIENTE = { Noche: 12, '24 horas': 8 };

export function derivarFechaLegado(hora, registro, { ts = null } = {}) {
  const creado = registro?.creado ? new Date(registro.creado) : null;
  if (!creado || Number.isNaN(creado.getTime())) {
    const base = ts ? new Date(ts) : new Date();
    return { fecha: fechaISOLocal(base), inferida: true, ambigua: true };
  }
  const pivote = PIVOTE_DIA_SIGUIENTE[registro?.turno];
  const fechaCreado = fechaISOLocal(creado);
  if (pivote == null || !RE_HORA.test(hora || '')) {
    return { fecha: fechaCreado, inferida: true, ambigua: pivote == null };
  }
  // El turno cruza medianoche: la parte del turno que va desde el pivote hasta
  // las 23:59 es el día de creación; lo que va de 00:00 al pivote es el día
  // siguiente. El pivote es el mismo que usa finDeTurnoRegistro.
  const h = Number(hora.slice(0, 2));
  const creadoDesdePivote = creado.getHours() >= pivote;
  const horaDelDiaSiguiente = h < pivote;
  if (creadoDesdePivote && horaDelDiaSiguiente) {
    const d = new Date(creado); d.setDate(d.getDate() + 1);
    return { fecha: fechaISOLocal(d), inferida: true, ambigua: false };
  }
  if (!creadoDesdePivote && !horaDelDiaSiguiente) {
    const d = new Date(creado); d.setDate(d.getDate() - 1);
    return { fecha: fechaISOLocal(d), inferida: true, ambigua: false };
  }
  return { fecha: fechaCreado, inferida: true, ambigua: false };
}

// El momento del hecho de una entrada: el guardado si existe, o el derivado.
export function momentoDeEntrada(entrada, registro = null) {
  const guardado = momentoValido(entrada?.momento);
  if (guardado) return guardado;
  if (!RE_HORA.test(entrada?.hora || '')) {
    // Sin hora no hay momento del hecho: se declara desconocido con la fecha
    // que se pueda derivar, y se ancla por registro para poder dibujarlo.
    const { fecha, inferida } = derivarFechaLegado(null, registro, { ts: entrada?.ts });
    return { fecha, hora: null, precision: 'desconocida', inferida: !!inferida };
  }
  const { fecha, inferida, ambigua } = derivarFechaLegado(entrada.hora, registro, { ts: entrada?.ts });
  const m = { fecha, hora: entrada.hora, precision: 'exacta', inferida: !!inferida };
  return ambigua ? { ...m, ambigua: true } : m;
}

// ── Orden de PRESENTACIÓN (cronología, relato, texto copiable) ──────────────
// Clave de orden. Un evento sin hora se ancla por su momento de REGISTRO para
// poder dibujarlo en su lugar aproximado; `anclada` dice que fue así, para que
// ningún consumidor lo lea como una hora del hecho.
export function claveOrden(entrada, registro = null) {
  const m = momentoDeEntrada(entrada, registro);
  if (m.hora) return { clave: `${m.fecha} ${m.hora}`, anclada: false, momento: m };
  const ts = Number.isFinite(entrada?.ts) ? entrada.ts : null;
  const ref = ts != null ? new Date(ts) : null;
  const clave = ref ? `${fechaISOLocal(ref)} ${horaLocalHHMM(ref)}` : `${m.fecha} 00:00`;
  return { clave, anclada: true, momento: m };
}

// Orden visual ESTABLE: por momento del hecho cuando se conoce, por momento de
// registro cuando no, y desempate por `ts` y por `id` para que dos entradas del
// mismo minuto no se muevan entre renders.
export function ordenarPorHecho(entradas, registro = null) {
  return [...(entradas || [])]
    .map((e, i) => ({ e, i, k: claveOrden(e, registro) }))
    .sort((a, b) => (
      a.k.clave.localeCompare(b.k.clave)
      || (a.e?.ts || 0) - (b.e?.ts || 0)
      // A igual momento y ts (dos entradas del mismo dictado, registradas en
      // el mismo instante), manda el orden de REGISTRO (el array), no el id: el
      // id es aleatorio y hacía el orden de presentación no determinista
      // (revisión 2026-09-07). No se inventan segundos.
      || a.i - b.i
      || String(a.e?.id || '').localeCompare(String(b.e?.id || ''))
    ))
    .map((x) => x.e);
}

// ── Predicado CLÍNICO: ¿esto pasó después de aquello? ───────────────────────
// Estricto a propósito. Devuelve false (no "no sé") cuando no se puede afirmar:
// hora desconocida de cualquiera de los dos, o dos horas aproximadas tan
// cercanas que no prueban una secuencia. El llamador que necesite distinguir
// "no pasó después" de "no se puede saber" usa comparablesPorHecho.
export function comparablesPorHecho(a, b) {
  return !!(a?.hora && b?.hora && a.precision !== 'desconocida' && b.precision !== 'desconocida');
}

function minutosDe(m) {
  return Date.parse(`${m.fecha}T${m.hora}:00Z`) / 60000;
}

export function esPosteriorA(a, b) {
  if (!comparablesPorHecho(a, b)) return false;
  const diff = minutosDe(a) - minutosDe(b);
  if (!Number.isFinite(diff) || diff <= 0) return false;
  const aprox = a.precision === 'aproximada' || b.precision === 'aproximada';
  return aprox ? diff > TOLERANCIA_APROXIMADA_MIN : true;
}

// ── Zona horaria y cambios de fecha ─────────────────────────────────────────
// El momento se guarda en HORA LOCAL DE PARED (fecha + hora + zona), que es lo
// que la autora vio en el reloj, y el orden se resuelve comparando esa pared:
// así el cruce de medianoche queda bien sin depender de convertir a instante.
// El instante solo hace falta para medir duraciones, y ahí aparecen los dos
// bordes del cambio de hora (en Chile la transición ocurre a las 24:00):
//   · 'inexistente': esa hora no ocurrió (adelanto). Se reporta y se resuelve
//     al instante inmediatamente posterior al salto.
//   · 'ambigua': esa hora ocurrió dos veces (atraso). Se reporta y se resuelve
//     a la PRIMERA. Nunca se inventa cuál de las dos fue.
// La zona es la del dispositivo al registrar; se guarda con el momento para que
// un registro leído en otra zona no cambie de hora al mirarlo.
// Desplazamiento de una zona en un instante dado, leído de la base IANA del
// runtime (Intl). Es la única fuente: no hay tabla de cambios de hora acá.
function offsetEnZona(zona, ms) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: zona, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p = {};
  for (const x of dtf.formatToParts(new Date(ms))) p[x.type] = x.value;
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second) - ms;
}

// Los instantes que corresponden a una hora de pared en una zona: ninguno si la
// hora no existió (adelanto), dos si ocurrió dos veces (atraso), uno normalmente.
// Se prueban los desplazamientos vigentes un día antes y un día después, que es
// lo que acota cualquier transición.
function instantesDePared(zona, fecha, hora) {
  const [y, m, d] = fecha.split('-').map(Number);
  const [hh, mm] = hora.split(':').map(Number);
  const pared = Date.UTC(y, m - 1, d, hh, mm);
  const candidatos = new Set([
    pared - offsetEnZona(zona, pared - 86400000),
    pared - offsetEnZona(zona, pared + 86400000),
  ]);
  return [...candidatos].filter((t) => offsetEnZona(zona, t) === pared - t).sort((a, b) => a - b);
}

export function analizarHoraLocal(fecha, hora, zona = zonaDelDispositivo()) {
  if (!RE_FECHA.test(fecha || '') || !RE_HORA.test(hora || '')) return { ms: null, anomalia: 'invalida' };
  let instantes;
  try {
    instantes = instantesDePared(zona || 'UTC', fecha, hora);
  } catch {
    return { ms: null, anomalia: 'invalida' }; // zona desconocida para el runtime
  }
  if (instantes.length === 0) {
    // Esa hora no ocurrió. Se resuelve al instante inmediatamente posterior al
    // salto, que es el primer momento real que la autora pudo vivir.
    const [y, m, d] = fecha.split('-').map(Number);
    const [hh, mm] = hora.split(':').map(Number);
    const pared = Date.UTC(y, m - 1, d, hh, mm);
    const ms = pared - offsetEnZona(zona || 'UTC', pared + 86400000);
    return { ms, anomalia: 'inexistente' };
  }
  if (instantes.length > 1) return { ms: instantes[0], anomalia: 'ambigua' };
  return { ms: instantes[0], anomalia: null };
}

// Los días que puede tener el HECHO dentro de un turno. En Largo es uno solo
// (no cruza medianoche) y la interfaz no pregunta; en Noche y 24 horas son dos,
// y ahí sí tiene sentido ofrecer "fue ayer".
export function diasDelTurno(registro, ahora = new Date()) {
  const creado = registro?.creado ? new Date(registro.creado) : null;
  if (!creado || Number.isNaN(creado.getTime())) return [];
  const pivote = PIVOTE_DIA_SIGUIENTE[registro?.turno];
  const dia0 = new Date(creado); dia0.setHours(0, 0, 0, 0);
  if (pivote == null) return [{ fecha: fechaISOLocal(dia0), label: etiquetaDia(dia0, ahora) }];
  const inicio = creado.getHours() >= pivote ? dia0 : new Date(dia0.getTime() - 86400000);
  const fin = new Date(inicio.getTime() + 86400000);
  return [inicio, fin].map((d) => ({ fecha: fechaISOLocal(d), label: etiquetaDia(d, ahora) }));
}

function etiquetaDia(d, ahora) {
  const hoy = fechaISOLocal(ahora);
  const ayer = fechaISOLocal(new Date(ahora.getTime() - 86400000));
  const f = fechaISOLocal(d);
  if (f === hoy) return 'Hoy';
  if (f === ayer) return 'Ayer';
  return `${d.getDate()}/${d.getMonth() + 1}`;
}
